/**
 * Shared helpers for post profile save flows.
 */
import type { RefObject } from "react";
import api from "config/axiosConfig";
import type { CoauthorSuggestion } from "types/coauthor";
import type { EntityCardRef } from "components/Entities/EntityCardBase";
import { apiRoutes } from "util/apiRoutes";
import type { EntityKind } from "util/posts";

function mapCoauthorPair(coauthor: CoauthorSuggestion): { id: number; type: CoauthorSuggestion["type"] } {
  return {
    id: coauthor.id,
    type: coauthor.type,
  };
}

/**
 * Flushes any pending media reorder state from the card before the parent save runs.
 */
export async function applyPendingMediaOrder(
  cardRef: RefObject<EntityCardRef>
): Promise<boolean> {
  const didApply = await cardRef.current?.applyMediaOrderChanges();
  return didApply !== false;
}

/**
 * Persists coauthor additions and removals when the post remains non-anonymous.
 */
export async function syncPostCoauthors(
  kind: EntityKind,
  entityId: number | string,
  addedCoauthors: CoauthorSuggestion[],
  removedCoauthors: CoauthorSuggestion[],
  privacy: string
): Promise<void> {
  if (privacy === "anonymous") {
    return;
  }

  if (addedCoauthors.length === 0 && removedCoauthors.length === 0) {
    return;
  }

  await api.post(apiRoutes.coauthors.request(kind, entityId), {
    addedCoauthors: addedCoauthors.map(mapCoauthorPair),
    removedCoauthors: removedCoauthors.map(mapCoauthorPair),
  });
}
